import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Star } from 'lucide-react';

export const Stars = ({ value, onChange, size = 'w-6 h-6', testid = 'stars' }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} aria-label={`${n} estrellas`}
        className={onChange ? 'transition-transform hover:scale-110' : 'cursor-default'} data-testid={`${testid}-${n}`}>
        <Star className={`${size} ${n <= value ? 'fill-stilo-gold text-stilo-gold' : 'text-stilo-stone'}`} strokeWidth={1.25} />
      </button>
    ))}
  </div>
);

const STATUS = { pending: 'Pendiente de revisar', approved: 'Visible en la página principal', hidden: 'Oculta' };

const DesignerControls = ({ review, setReview }) => {
  const setStatus = async (status) => {
    try {
      await api.post(`/reviews/${review.id}/status`, { status });
      setReview({ ...review, status });
      toast.success(status === 'approved' ? 'Se mostrará en la página principal' : 'Valoración oculta');
    } catch { toast.error('No se pudo actualizar'); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 mt-5">
      <span className="text-xs text-stilo-ink2" data-testid="review-status">{STATUS[review.status]}</span>
      {review.status !== 'approved' && <button onClick={() => setStatus('approved')} className="btn-stilo" data-testid="review-approve">Mostrar en la home</button>}
      {review.status !== 'hidden' && <button onClick={() => setStatus('hidden')} className="px-4 py-2 border border-stilo-ink/20 text-sm hover:border-stilo-ink" data-testid="review-hide">Ocultar</button>}
    </div>
  );
};

export const ReviewCard = ({ cid, isDesigner, closed }) => {
  const [review, setReview] = useState(undefined);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get(`/consultations/${cid}/review`).then(r => setReview(r.data)).catch(() => setReview(null)); }, [cid]);

  if (review === undefined || (!review && (isDesigner || !closed))) return null;

  const submit = async () => {
    if (!rating) { toast.error('Elige de 1 a 5 estrellas'); return; }
    setSending(true);
    try {
      const r = await api.post(`/consultations/${cid}/review`, { rating, comment });
      setReview(r.data); toast.success('¡Gracias por tu valoración!');
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo enviar'); } finally { setSending(false); }
  };

  return (
    <section className="mt-8 border border-stilo-gold/40 bg-white p-6 lg:p-8" data-testid="review-card">
      <p className="overline">{isDesigner ? 'Valoración de la clienta' : 'Tu valoración'}</p>
      {review ? (
        <>
          <div className="mt-3"><Stars value={review.rating} testid="review-stars" /></div>
          {review.comment && <p className="mt-3 text-stilo-ink2 leading-relaxed whitespace-pre-wrap" data-testid="review-comment">«{review.comment}»</p>}
          {isDesigner ? <DesignerControls review={review} setReview={setReview} />
            : <p className="mt-4 text-sm text-stilo-ink3" data-testid="review-thanks">Gracias, {review.display_name.split(' ')[0]}. Tu opinión me ayuda muchísimo.</p>}
        </>
      ) : (
        <>
          <h3 className="font-serif text-3xl mt-2">¿Qué te ha parecido la asesoría?</h3>
          <div className="mt-5"><Stars value={rating} onChange={setRating} testid="review-input-stars" /></div>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4} maxLength={1500}
            placeholder="Cuéntame cómo te has sentido y qué te ha ayudado más…"
            className="mt-5 w-full border border-stilo-ink/15 p-3 bg-stilo-bg focus:outline-none focus:border-stilo-ink" data-testid="review-input-comment" />
          <button onClick={submit} disabled={sending} className="btn-stilo mt-4 disabled:opacity-50" data-testid="review-submit">{sending ? 'Enviando…' : 'Enviar valoración'}</button>
        </>
      )}
    </section>
  );
};
